import {IDataClientService} from "../../platform/services/data/DataClientService";
import {IAuthService} from "../../platform/services/auth/AuthService";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {
    createIssue,
    resolveIssueWithNote,
    dismissIssue,
    reopenIssue,
    archiveIssue,
    unarchiveIssue,
    deleteIssue,
} from "../../graphql/reliability/mutations";
import {resolveIssueWithCorrectiveTask} from "../../graphql/tasks/mutations";

import {Issue, CreateIssueInput, IssueSourceServer} from "./types";

const ENDPOINT = "pgServer";

type Deps = {dataClientService: IDataClientService; authService: IAuthService};

/**
 * All GQL mutations for Issues. Instantiated by action functions.
 * DS auto-syncs via subscriptions after any mutation — no manual _upsert needed.
 */
export class IssueGqlProvider {
    private readonly dataClientService: IDataClientService;
    private readonly authService: IAuthService;
    private readonly wsId: string;

    constructor(deps: Deps, wsId: string) {
        this.dataClientService = deps.dataClientService;
        this.authService = deps.authService;
        this.wsId = wsId;
    }

    create(input: {
        title: string;
        description?: string | null;
        locationId?: string | null;
        source?: IssueSourceServer;
        images?: any;
        auditRunId?: string | null;
        sourceRef?: any;
    }): Promise<Issue> {
        const payload: CreateIssueInput = {
            workspaceId: this.wsId,
            source: input.source ?? 'manual_report',
            title: input.title,
            description: input.description ?? null,
            locationId: input.locationId ?? null,
            images: input.images,
            auditRunId: input.auditRunId ?? null,
            sourceRef: input.sourceRef ?? null,
        };
        return queryGqlApi(
            createIssue, {input: payload},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<Issue>;
    }

    resolveWithNote(id: string, note: string): Promise<Issue> {
        return queryGqlApi(
            resolveIssueWithNote, {input: {id, note}},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<Issue>;
    }

    resolveWithCorrectiveTask(id: string, correctiveTaskId: string): Promise<Issue> {
        return queryGqlApi(
            resolveIssueWithCorrectiveTask, {input: {id, correctiveTaskId}},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<Issue>;
    }

    dismiss(id: string, comment?: string): Promise<Issue> {
        return queryGqlApi(
            dismissIssue, {input: {id, comment: comment ?? null}},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<Issue>;
    }

    reopen(id: string): Promise<Issue> {
        return queryGqlApi(
            reopenIssue, {id},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<Issue>;
    }

    archive(id: string): Promise<Issue> {
        return queryGqlApi(
            archiveIssue, {id},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<Issue>;
    }

    unarchive(id: string): Promise<Issue> {
        return queryGqlApi(
            unarchiveIssue, {id},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<Issue>;
    }

    async delete(id: string): Promise<void> {
        await queryGqlApi(
            deleteIssue, {id},
            this.dataClientService, this.authService, ENDPOINT,
        );
    }
}
